import { formatDateMain } from './date';
import dayjs, { Dayjs } from 'dayjs';

export interface OrderDateDisplayOptions {
  /** The start date/time of the pickup or delivery window */
  from: Date | string | Dayjs;
  /** The end date/time of the pickup or delivery window */
  to: Date | string | Dayjs;
  /** Optional translation function for "Today" and "Tomorrow" */
  translate?: (key: string, fallback?: string) => string;
  /** Timezone for date formatting (optional) */
  timezone?: 'Asia/Riyadh';
  /** When true, swaps time range order so it reads correctly right-to-left */
  isRTL?: boolean;
}

export interface OrderDateDisplay {
  /** Time range only: "7:00 AM - 9:00 AM" */
  timeOnly: string;
  /** Compact time when both ends are on the hour: "3 - 5pm"; otherwise same as `timeOnly`. */
  timeCompactOnly: string;
  /** Date only (auto: can be relative or exact): `Today` or `Jan 6` */
  dateAutoOnly: string;
  /** Date only (always exact, never relative): `Jan 6` even if today */
  dateOnly: string;
  /** Relative date if applicable: `Today`, `Tomorrow`, or `''` if not relative */
  relativeDateOnly: string;
  /** Full display with date (auto: relative or exact) and time: `Today (7:00 AM - 9:00 AM)` or `Jan 6 (7:00 AM - 9:00 AM)` */
  fullDateAuto: string;
  /** Full display with date (always exact, never relative) and time: `Jan 6 (7:00 AM - 9:00 AM)` */
  fullDate: string;
  /** Full display with auto date, without parentheses: `Today, 7:00 AM - 9:00 AM` */
  fullWithoutParenthesesAuto: string;
  /** Full display with exact date, without parentheses: `Jan 6, 7:00 AM - 9:00 AM` */
  fullWithoutParentheses: string;
  /** Raw dayjs objects for custom formatting */
  raw: {
    from: Dayjs;
    to: Dayjs;
  };
}

/**
 * Universal function for displaying order date/time ranges (from/to).
 * Returns all possible display formats - the caller decides which to use.
 *
 * @param options - Date range and formatting options
 * @returns All possible display formats
 *
 * @example
 * ```ts
 * const display = getOrderDisplayFromTo({
 *   from: order.dates.pickup.from,
 *   to: order.dates.pickup.to,
 *   translate: (key, fallback) => t(key, fallback),
 * });
 *
 * // Use any format you need:
 * console.log(display.fullDateAuto); // "Today (7:00 AM - 9:00 AM)" or "Jan 6 (7:00 AM - 9:00 AM)"
 * console.log(display.fullDate); // "Jan 6 (7:00 AM - 9:00 AM)" (always exact date)
 * console.log(display.timeOnly); // "7:00 AM - 9:00 AM"
 * console.log(display.timeCompactOnly); // "7 - 9 AM" when on the hour
 * console.log(display.dateAutoOnly); // "Today" or "Jan 6"
 * console.log(display.dateOnly); // "Jan 6" (always exact date, never "Today")
 * ```
 */
export function formatCompactTimeRange(
  fromDate: Dayjs,
  toDate: Dayjs,
  isRTL?: boolean,
): string {
  const LRM = '\u200E';
  const fullFrom = `${LRM}${fromDate.format('h:mm A')}`;
  const fullTo = `${LRM}${toDate.format('h:mm A')}`;
  const fullRange = isRTL ? `${fullTo} - ${fullFrom}` : `${fullFrom} - ${fullTo}`;

  if (fromDate.minute() !== 0 || toDate.minute() !== 0) {
    return fullRange;
  }

  const fromPeriod = fromDate.format('A');
  const toPeriod = toDate.format('A');
  const fromHour = fromDate.format('h');
  const toHour = toDate.format('h');

  if (isRTL) {
    if (fromPeriod === toPeriod) {
      return `${LRM}${toHour} - ${fromHour}${fromPeriod.toLowerCase()}`;
    }
    return `${LRM}${toHour}${toPeriod.toLowerCase()} - ${fromHour}${fromPeriod.toLowerCase()}`;
  }

  if (fromPeriod === toPeriod) {
    return `${LRM}${fromHour} - ${toHour}${fromPeriod.toLowerCase()}`;
  }
  return `${LRM}${fromHour}${fromPeriod.toLowerCase()} - ${toHour}${toPeriod.toLowerCase()}`;
}

export const getOrderDisplayFromTo = (options: OrderDateDisplayOptions): OrderDateDisplay => {
  const { from, to, translate, timezone, isRTL } = options;

    // Convert to dayjs objects
    const fromDate = dayjs(from);
    const toDate = dayjs(to);

    // Get time range string
    // LRM (Left-to-Right Mark) ensures individual time values display correctly in RTL
    // In RTL, swap order so reading right-to-left gives "from → to"
  const LRM = '\u200E';
  const fromTime = `${LRM}${fromDate.format('h:mm A')}`;
  const toTime = `${LRM}${toDate.format('h:mm A')}`;
  const timeOnly = isRTL ? `${toTime} - ${fromTime}` : `${fromTime} - ${toTime}`;
  const timeCompactOnly = formatCompactTimeRange(fromDate, toDate, isRTL);

  // Get relative date (Today/Tomorrow) or formatted date
  // Note: 'date' format automatically includes year if not current year
  const dateFormatted = formatDateMain({
        date: fromDate.toDate(),
        format: 'relative_without_time',
        ifDateisNotRelative: 'date',
        timezone,
      });

  // Get exact date (never relative)
  // Note: 'date' format automatically includes year if not current year
  const exactDate = formatDateMain({
        date: fromDate.toDate(),
    format: 'date',
        timezone,
      });

  // Translate "Today" and "Tomorrow" if translation function provided
  let dateAutoOnly = dateFormatted;
  const dateOnly = exactDate;
  let relativeDateOnly = '';

  if (dateFormatted === 'Today') {
    relativeDateOnly = 'Today';
    if (translate) {
      dateAutoOnly = translate('date.today', 'Today');
      relativeDateOnly = dateAutoOnly;
    }
  } else if (dateFormatted === 'Tomorrow') {
    relativeDateOnly = 'Tomorrow';
    if (translate) {
      dateAutoOnly = translate('date.tomorrow', 'Tomorrow');
      relativeDateOnly = dateAutoOnly;
    }
  }

  // Full display with date (auto: relative or exact) and time in parentheses
  const fullDateAuto = `${dateAutoOnly} (${timeOnly})`;

  // Full display with date (always exact, never relative) and time in parentheses
  const fullDate = `${dateOnly} (${timeOnly})`;

  // Full display with auto date, without parentheses
  const fullWithoutParenthesesAuto = `${dateAutoOnly}, ${timeOnly}`;

  // Full display with exact date, without parentheses
  const fullWithoutParentheses = `${dateOnly}, ${timeOnly}`;

  return {
    timeOnly,
    timeCompactOnly,
    dateAutoOnly,
    dateOnly,
    relativeDateOnly,
    fullDateAuto,
    fullDate,
    fullWithoutParenthesesAuto,
    fullWithoutParentheses,
    raw: {
      from: fromDate,
      to: toDate,
    },
  };
};
